import { DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import FilterListItems from 'advanced-filters/components/FilterListItems';
import { BASE_COMPARATOR_OPTIONS, type BaseOperatorType } from 'advanced-filters/data';
import { Button, Segmented } from 'antd';
import clsx from 'clsx';
import React, { useCallback } from 'react';

import FilterListItemSeperator from '../FilterListItemSeperator';
import styles from './BucketSelector.module.scss';

interface BucketTitleProps {
  level: number;
  operator: BaseOperatorType;
  showDelete: boolean;
  canEdit: boolean;
  onOperatorChange?: (level: number, operator: BaseOperatorType) => void;
  onDeleteBucket?: (level: number) => void;
}

function BucketTitle({
  level,
  operator,
  showDelete,
  canEdit,
  onOperatorChange,
  onDeleteBucket,
}: BucketTitleProps) {
  const onChange = useCallback((value: string | number) => {
    onOperatorChange?.(level, value as BaseOperatorType);
  }, [onOperatorChange, level]);

  return (
    <div className={styles.bucketTitle}>
      <span>
        <FilterOutlined className={styles.filterIcon} />
        Filter Bucket
        {' '}
        {level + 1}
      </span>
      <div className={styles.rightControls}>
        {showDelete && canEdit && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            className={styles.deleteButton}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteBucket?.(level);
            }}
          />
        )}
        {canEdit && (
          <Segmented
            value={operator}
            onChange={onChange}
            options={[
              {
                value: BASE_COMPARATOR_OPTIONS.or,
                label: 'OR',
                className: clsx(operator === BASE_COMPARATOR_OPTIONS.or && styles.activeOperator),
              },
              {
                value: BASE_COMPARATOR_OPTIONS.and,
                label: 'AND',
                className: clsx(operator === BASE_COMPARATOR_OPTIONS.and && styles.activeOperator),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

interface Bucket {
  level: number;
  operator: BaseOperatorType;
  criteria: any[]; // Use the actual type from your application
}

interface BucketSelectorProps {
  buckets: Bucket[];
  textOnly?: boolean;
  canEdit?: boolean;
  baseComparator: BaseOperatorType;
  selectedLevel?: number;
  onLevelClick?: (level: number) => void;
  onOperatorChange?: (level: number, operator: BaseOperatorType) => void;
  onDeleteBucket?: (level: number) => void;
}

export default function BucketSelector({
  buckets = [],
  textOnly = false,
  canEdit = true,
  baseComparator,
  selectedLevel,
  onLevelClick,
  onOperatorChange,
  onDeleteBucket,
}: BucketSelectorProps) {
  const handleLevelClick = useCallback((level: number) => {
    onLevelClick?.(level);
  }, [onLevelClick]);

  return (
    <div className={styles.bucketList}>
      {buckets.map((bucket, index) => (
        <React.Fragment key={bucket.level}>
          <div
            className={clsx(styles.bucket, {
              [styles.selectedBucket as string]: bucket.level === selectedLevel && canEdit,
            })}
            onClick={() => {
              handleLevelClick(bucket.level);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleLevelClick(bucket.level);
              }
            }}
            aria-label={`Filter Bucket ${bucket.level + 1}`}
            key={bucket.level}
          >
            {!textOnly && (
              <BucketTitle
                level={bucket.level}
                operator={bucket.operator}
                canEdit={canEdit}
                showDelete={buckets.length > 1}
                onOperatorChange={onOperatorChange}
                onDeleteBucket={onDeleteBucket}
              />
            )}
            <FilterListItems
              filters={bucket.criteria}
              operator={bucket.operator}
              textOnly={textOnly}
              canEdit={canEdit}
            />
          </div>
          {index < buckets.length - 1 && !textOnly && (
            <FilterListItemSeperator
              operator={baseComparator}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
